// /settings/integrations - the connections surface as a first-class page:
// verdicts from the one server aggregate, scope disclosure,
// connect/disconnect. The one staff-readable tab of the Settings area.

import { useState, type ReactElement } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  INTEGRATION_GROUPS,
  integrationCounts,
  resolveIntegrations,
} from "../../data/integrations";
import type { Integration } from "../../data/types";
import { Dialog } from "../../components/chrome/Dialog";
import { GIcon } from "../../components/chrome/GIcon";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";
import { useScreens } from "../../components/chrome/screens";
import { AnthropicKeySection } from "../../components/integrations/AnthropicKeySection";
import { AiModelsSection } from "../../components/integrations/AiModelsSection";
import { GoogleWorkspaceSection } from "../../components/integrations/GoogleWorkspaceSection";
import { DriveImportSection } from "../../components/integrations/DriveImportSection";
import { MailSection } from "../../components/integrations/MailSection";
import { PortalSection } from "../../components/integrations/PortalSection";
import { useGoogleConnect } from "../../hooks/useGoogleConnect";
import { useGoogleDisconnect } from "../../hooks/useGoogleDisconnect";
import { useIntegrationsStatus } from "../../hooks/useIntegrationsStatus";
import { useCurrentUser } from "../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { SettingsNav } from "../settings/SettingsNav";

const GEAR = String.fromCharCode(9881);

// Rows whose credentials an admin can edit here; a module's rows (Stripe)
// merge in through the screens registry below. A row in neither has no
// gear, and the dialog takes its heading from the row itself, so the head
// can never name something other than what was clicked.
const CONFIGURABLE: Record<string, () => ReactElement> = {
  anthropic: () => (
    <>
      <AnthropicKeySection />
      <AiModelsSection />
    </>
  ),
  "google-workspace": () => (
    <>
      <GoogleWorkspaceSection />
      <DriveImportSection />
    </>
  ),
  mail: () => <MailSection />,
  portal: () => <PortalSection />,
};

// A block whose setting is shared by all of its rows configures from its
// HEAD, for the same reason the grant's consent controls do: the Shared
// Drive and workbook ids serve Workspace, Drive and Sheets alike, and a
// gear on one row would lie about the scope of the change.
const BLOCK_CONFIGURABLE: Record<string, string> = { "google-sa": "google-workspace" };

function fmtSync(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusPill(status: Integration["status"]) {
  const cls = status === "live" ? "live" : status === "action_required" ? "action" : "unknown";
  const label =
    status === "live"
      ? "Live"
      : status === "action_required"
        ? "Action req"
        : status === "configured"
          ? "Configured"
          : status === "not_configured"
            ? "Not configured"
            : "Not verified";
  return (
    <span className={"v2-status-pill " + cls}>
      <span className="dot" />
      {label}
    </span>
  );
}

/** The head's one truth about a grant family. The server grades scope
 *  coverage PER PRODUCT, so the family is not guaranteed uniform: a
 *  grant missing only the Gmail scopes is action_required on one row
 *  and live on two. The head must quote the member that needs
 *  attention, never whichever happens to be items[0]. */
function grantFacts(items: Integration[]) {
  const flagged = items.find((i) => i.status === "action_required");
  return {
    account: items.find((i) => i.account)?.account,
    status: flagged
      ? ("action_required" as const)
      : items.some((i) => i.status === "live")
        ? ("live" as const)
        : items[0].status,
    detail: (flagged ?? items[0]).detail,
  };
}

/** The staff grant is ONE credential covering Gmail, Calendar and
 *  Contacts, so the consent controls and the grant facts sit on the
 *  block head, never on the three rows - a per-row button lies about
 *  the scope of the action. Hardcoded to the google-oauth block; the
 *  next grant family (M365) is the day this earns a data flag. */
function GrantActions({ fam }: { fam: ReturnType<typeof grantFacts> }) {
  const google = useGoogleConnect();
  const disconnect = useGoogleDisconnect();
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const showConnect = fam.status === "action_required";
  const showDisconnect = fam.status === "live" && isAdmin;
  const hasErrors = Boolean(
    google.error ||
    disconnect.isError ||
    (disconnect.isSuccess && disconnect.data?.revoked === false),
  );
  // An empty flex container still occupies its gap - with nothing to
  // say, say nothing and keep the blocks aligned.
  if (!fam.account && !fam.detail && !showConnect && !showDisconnect && !hasErrors) {
    return null;
  }
  return (
    <div className="v2-conn-group-actions">
      {fam.account && <span className="fact">Connected as {fam.account}</span>}
      {fam.detail && <span className="fact">{fam.detail}</span>}
      {showConnect && (
        <button className="cta" onClick={() => google.connect()} disabled={google.connecting}>
          {google.connecting ? "Opening Google..." : "Connect Google"}
        </button>
      )}
      {showDisconnect && (
        <button
          className="cta danger"
          disabled={disconnect.isPending}
          onClick={() => {
            if (
              window.confirm(
                "Disconnect Google? DuraLex loses access to Gmail, " +
                  "Calendar and Contacts until an admin reconnects. " +
                  "Case data stays in DuraLex.",
              )
            ) {
              disconnect.mutate();
            }
          }}
        >
          {disconnect.isPending ? "Disconnecting..." : "Disconnect Google"}
        </button>
      )}
      {google.error && <span className="v2-gmail-status error">{google.error}</span>}
      {disconnect.isError && (
        <span className="v2-gmail-status error">
          {disconnect.error instanceof Error ? disconnect.error.message : "Could not disconnect"}
        </span>
      )}
      {disconnect.isSuccess && disconnect.data?.revoked === false && (
        <span className="v2-gmail-status error">
          Local access cleared - Google did not confirm; remove it at myaccount.google.com
        </span>
      )}
    </div>
  );
}

export default function IntegrationsIndex() {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  // Which row's gear is open. The label rides along so the dialog head
  // and the row can never disagree about what is being configured.
  const [configuring, setConfiguring] = useState<{ id: string; label: string } | null>(null);
  // A write in flight carries a one-time answer the dialog is the only
  // place to read - a stray click on the scrim must not lose it.
  const busy = useIsMutating({ mutationKey: ["integrations", configuring?.id ?? "__idle__"] }) > 0;
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const configurable: Record<string, () => ReactElement> = Object.assign(
    {},
    CONFIGURABLE,
    ...useScreens().map((s) => s.integrationSettings ?? {}),
  );
  const statusQ = useIntegrationsStatus();
  const resolved = resolveIntegrations(statusQ.data);
  // Plans are not connections: a row is roadmap only while no probe can
  // say anything about it, so the split follows the resolved status and
  // a row leaves the roadmap section the day the server reports on it.
  const connections = resolved.filter((i) => i.status !== "roadmap");
  const roadmap = resolved.filter((i) => i.status === "roadmap");

  if (statusQ.isLoading) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <SettingsNav />
          <SkeletonGroup label="Loading connections">
            <Skeleton width="30%" height={22} />
            <Skeleton width="48%" height={72} radius={10} />
            <Skeleton width="48%" height={72} radius={10} />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  return (
    <main className="cc-main">
      <div className="cc-body">
        <SettingsNav />

        <div className="cc-index-head">
          <span className="title">Integrations</span>
          <span className="count">{integrationCounts(resolved).pillText}</span>
          <span className="blurb">What DuraLex is connected to, as the server verifies it.</span>
        </div>

        {statusQ.isError && (
          <div
            className="cc-card"
            style={{ padding: "10px 18px", marginBottom: 12, color: "var(--warn-600)" }}
          >
            Could not reach the server - every verifiable row shows as Not verified.
          </div>
        )}

        <div className="intg-grid">
          {INTEGRATION_GROUPS.map((g) => {
            const items = connections.filter((i) => i.group === g.id);
            if (items.length === 0) return null;
            const fam = g.id === "google-oauth" ? grantFacts(items) : null;
            return (
              <div key={g.id} className="v2-conn-group">
                <div className="v2-conn-group-head">
                  {g.label}
                  {BLOCK_CONFIGURABLE[g.id] && isAdmin && (
                    <button
                      type="button"
                      className="v2-conn-gear"
                      aria-label={`Configure ${g.label}`}
                      onClick={() =>
                        setConfiguring({ id: BLOCK_CONFIGURABLE[g.id], label: g.label })
                      }
                    >
                      {GEAR}
                    </button>
                  )}
                </div>
                <div className="v2-conn-group-sub">{g.subtitle}</div>
                {fam && <GrantActions fam={fam} />}
                {items.map((i) => (
                  <div key={i.id} className="v2-conn-row">
                    <div className="icon">
                      <GIcon kind={i.iconKind} size={28} />
                    </div>
                    <div className="body">
                      <div className="name">
                        {i.label}
                        {statusPill(i.status)}
                        {configurable[i.id] && isAdmin && (
                          <button
                            type="button"
                            className="v2-conn-gear"
                            aria-label={`Configure ${i.label}`}
                            onClick={() => setConfiguring({ id: i.id, label: i.label })}
                          >
                            {GEAR}
                          </button>
                        )}
                      </div>
                      <div className="scope">{i.scope}</div>
                      {/* A fact the block head already states is not repeated on
                          a row - but a row's OWN fact (a partial grant's missing
                          scopes, a lone member's renewal) must still render. */}
                      {i.account && i.account !== fam?.account && (
                        <div className="last-sync">Connected as {i.account}</div>
                      )}
                      {i.detail && i.detail !== fam?.detail && (
                        <div className="last-sync">{i.detail}</div>
                      )}
                      {i.lastSyncAt && (
                        <div className="last-sync">Last sync {fmtSync(i.lastSyncAt)}</div>
                      )}
                      <div className="actions">
                        {i.id === "uscis" && (
                          <Link className="cta ghost" to="/settings/forms">
                            Open Forms
                          </Link>
                        )}
                        {i.accessSummary && (
                          <button className="cta ghost" onClick={() => toggle(i.id)}>
                            {expanded[i.id] ? "Hide details" : "What we'll access"}
                          </button>
                        )}
                      </div>
                      {expanded[i.id] && (
                        <div className="v2-conn-disclosure">
                          <div>{i.accessSummary}</div>
                          {i.accessGranted && (
                            <>
                              <b>Granted</b>
                              <ul>
                                {i.accessGranted.map((g2, k) => (
                                  <li key={k}>{g2}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {i.accessNeverGranted && (
                            <>
                              <b>Never granted</b>
                              <ul>
                                {i.accessNeverGranted.map((g2, k) => (
                                  <li key={k}>{g2}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {i.rawScopes && i.rawScopes.length > 0 && (
                            <>
                              <b>Raw OAuth scopes</b>
                              <ul>
                                {i.rawScopes.map((s, k) => (
                                  <li key={k}>
                                    <code>{s}</code>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <Dialog
          open={configuring !== null}
          onClose={() => setConfiguring(null)}
          title={configuring?.label ?? ""}
          locked={busy}
        >
          {configuring ? configurable[configuring.id]() : null}
        </Dialog>

        {roadmap.length > 0 && (
          <section className="intg-roadmap">
            <div className="v2-conn-group-head">Roadmap</div>
            <div className="v2-conn-group-sub">
              Planned connectors - not built yet, so there is no state to verify and nothing is
              accessed.
            </div>
            <div className="intg-roadmap-rows">
              {roadmap.map((i) => (
                <div key={i.id} className="intg-roadmap-row">
                  <GIcon kind={i.iconKind} size={20} />
                  <div>
                    <div className="name">{i.label}</div>
                    <div className="scope">{i.scope}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
